import { IconButton, LuIcon, Menu, stopPropagation } from "@overmap-ai/blocks"
import { FileCard as OvermapFileCard, type FileCardProps as OvermapFileCardProps } from "@overmap-ai/forms"
import { saveAs } from "file-saver"
import { memo, type MouseEventHandler, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"

export interface FileCardProps extends Omit<OvermapFileCardProps, "file"> {
	file: File | null
	error?: string
	onDelete?: () => void
}

export const FileCard = memo((props: FileCardProps) => {
	const { file, onDelete, error, onClick, ...rest } = props

	const handleView = useCallback(() => {
		if (!file) return

		const objectUrl = URL.createObjectURL(file)
		window.open(objectUrl, "_blank")
		URL.revokeObjectURL(objectUrl)
	}, [file])

	const handleDownload = useCallback(() => {
		if (!file) return

		saveAs(file, file.name)
	}, [file])

	const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(
		(e) => {
			onClick?.(e)
			if (e.defaultPrevented) return
			handleView()
		},
		[handleView, onClick],
	)

	return (
		<OvermapFileCard
			file={file}
			error={error}
			style={{ cursor: file ? "pointer" : "unset" }}
			onClick={handleClick}
			{...rest}
			rightSlot={
				<div className="flex grow justify-end">
					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton
								variant="ghost"
								size="sm"
								accentColor="base"
								type="button"
								onClick={stopPropagation}
							>
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleView} disabled={!file}>
								<LuIcon icon={modelIconMapping.expand} />
								View
							</Menu.Item>
							<Menu.Item onSelect={handleDownload} disabled={!file}>
								<LuIcon icon={modelIconMapping.download} />
								Download
							</Menu.Item>
							{onDelete ? (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={onDelete} disabled={!file}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							) : null}
						</Menu.Content>
					</Menu.Root>
				</div>
			}
		/>
	)
})
FileCard.displayName = "FileCard"
